import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import { eventTypesForFilter, type EventFilter } from "@/lib/event-registry";

// VIB-1184: EventFilter re-eksporteres for eksisterende importstier; selve
// kontrakten (filter → event_types) bor i den sentrale event-registryen.
export type { EventFilter };

export interface ProjectEvent {
  id: string;
  project_id: string;
  user_id: string | null;
  event_type: string;
  entity_type: string | null;
  entity_id: string | null;
  payload: Record<string, unknown>;
  created_at: string;
  user?: {
    id: string;
    full_name: string;
    avatar_url: string | null;
  };
}

const PAGE_SIZE = 30;

export function useProjectEvents(
  projectId: string | undefined,
  filter: EventFilter = "all",
  searchUser: string = "",
  page: number = 0
) {
  return useQuery({
    queryKey: ["project-events", projectId, filter, searchUser, page],
    queryFn: async (): Promise<{ events: ProjectEvent[]; hasMore: boolean }> => {
      if (!projectId) return { events: [], hasMore: false };

      let query = supabase
        .from("project_events")
        .select("*, user:users(id, full_name, avatar_url)")
        .eq("project_id", projectId)
        .order("created_at", { ascending: false })
        .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);

      const eventTypes = eventTypesForFilter(filter);
      if (eventTypes) {
        query = query.in("event_type", eventTypes);
      }

      const { data, error } = await query;
      if (error) throw error;

      let events = (data ?? []) as ProjectEvent[];

      // Client-side filter on user name (simple substring match)
      if (searchUser.trim()) {
        const search = searchUser.toLowerCase();
        events = events.filter((e) =>
          e.user?.full_name?.toLowerCase().includes(search)
        );
      }

      return {
        events,
        hasMore: (data?.length ?? 0) === PAGE_SIZE,
      };
    },
    enabled: !!projectId,
  });
}

/**
 * Log an event to project_events. Call from mutation onSuccess callbacks.
 */
export async function logProjectEvent(params: {
  projectId: string;
  userId?: string | null;
  eventType: string;
  entityType?: string;
  entityId?: string;
  payload?: Record<string, unknown>;
}) {
  const { error } = await supabase.from("project_events").insert({
    project_id: params.projectId,
    user_id: params.userId ?? null,
    event_type: params.eventType,
    entity_type: params.entityType ?? null,
    entity_id: params.entityId ?? null,
    payload: params.payload ?? {},
  });

  if (error) {
    console.error("Failed to log project event:", error);
  }
}

/**
 * Mutation hook for logging events (with query invalidation).
 */
export function useLogProjectEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: logProjectEvent,
    onSuccess: async (_data, variables) => {
      await invalidateFor(queryClient, "project_events", { projectId: variables.projectId });
    },
  });
}
